import React, { useEffect, useRef, useState } from "react";
import { Card } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ReactCrop from "react-image-crop";
import { ReactSketchCanvas } from "react-sketch-canvas";
import { toast } from "react-toastify";
import {
  Button,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { getSettingsCompanyDetails } from "../../services/settingsServices";
import { createPartsByPhoto } from "../../services/PartsInventory";
import Utils from "../../utils";
import ConfigDB from "../../config";
import { useLocation, useNavigate } from "react-router-dom";
import AutoFillVehicle from "./AutoFillVehicle";

const AutofillParts = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const LangData = localStorage.getItem("lng");
  const data = localStorage.getItem(ConfigDB.data.login_data);
  const companyId = JSON.parse(data);
  const state = useLocation();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = useState(1);
  const [images, setImage] = useState([]);
  const [previewUrl, SetPreviewUrl] = useState([]);
  const [enlargedIndexes, setEnlargedIndexes] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const canvasRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const imgRef = useRef(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [crop, setCrop] = useState({ unit: "%", width: 50, height: 50 });
  const [highlights, setHighlights] = useState([]);
  const [selectedColor, setSelectedColor] = useState("#ff0000");
  const [isCropping, setIsCropping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadings, setIsLoadings] = useState(false);
  const [isLoadingColor, setIsLoadingColor] = useState(false);
  const [settingCompanyDetails, setSettingCompanyDetails] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [partsByPhotoData, setPartsByPhotoData] = useState("");

  const closeModal = () => {
    setIsModalOpen(false);
  };
  const handleAboutFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);

    if (selectedFiles.length > 0) {
      const formattedFiles = selectedFiles.map((file, index) => ({
        id: `${Date.now()}-${index}`,
        file,
        url: URL.createObjectURL(file),
      }));

      setImage((prevImages) => [...prevImages, ...formattedFiles]);

      const newPreviewUrls = formattedFiles.map((file) => file.url);
      SetPreviewUrl((prevUrls) => [...prevUrls, ...newPreviewUrls]);
    }
  };

  const toggleEnlarge = (index) => {
    if (enlargedIndexes.includes(index)) {
      setEnlargedIndexes(enlargedIndexes.filter((i) => i !== index));
    } else {
      setEnlargedIndexes([...enlargedIndexes, index]);
    }
  };

  const handleImageSelect = (file) => {
    setImage((prevFiles) =>
      prevFiles.map((f) => (f.id === file.id ? { ...f } : f))
    );
    setSelectedImage(file);
    setCrop(null);
    setRotation(0);
    setHighlights([]);
    setIsModalOpen(true);
  };

  const handleRemoveFile = (indexToRemove) => {
    setImage((prevFiles) =>
      prevFiles.filter((_, index) => index !== indexToRemove)
    );
    SetPreviewUrl((prevUrls) =>
      prevUrls.filter((_, index) => index !== indexToRemove)
    );

    if (images.length === 1) {
      fileInputRef.current.value = "";
    }
  };

  const onImageLoad = (img) => {
    imgRef.current = img;
  };

  const onImageClick = (e) => {
    const { offsetX, offsetY } = e.nativeEvent;
    const newHighlight = {
      x: offsetX,
      y: offsetY,
      radius: 20,
      color: selectedColor,
    };

    setHighlights([...highlights, newHighlight]);
  };

  const handleCrop = () => {
    setIsCropping(!isCropping);
  };

  const saveDrawing = async (fileId) => {
    setIsDrawing((prev) => !prev);
  };

  const getCroppedImage = async () => {
    if (!imgRef.current || !selectedImage) return;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    let cropX = 0,
      cropY = 0,
      cropWidth = image.naturalWidth,
      cropHeight = image.naturalHeight;

    if (completedCrop) {
      cropX = completedCrop.x * scaleX;
      cropY = completedCrop.y * scaleY;
      cropWidth = completedCrop.width * scaleX;
      cropHeight = completedCrop.height * scaleY;
    }

    const radians = (rotation * Math.PI) / 180;
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();

    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(radians);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    ctx.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight
    );

    ctx.restore();

    if (canvasRef.current) {
      try {
        const drawingDataURL = await canvasRef.current.exportImage("png");

        if (drawingDataURL) {
          const drawingImage = new Image();
          drawingImage.src = drawingDataURL;
          await new Promise((resolve) => (drawingImage.onload = resolve));

          ctx.drawImage(drawingImage, 0, 0, cropWidth, cropHeight);
        }
      } catch (error) {
        console.error("Error exporting drawing:", error);
      }
    }

    const editedImage = canvas.toDataURL("image/jpeg");

    setImage((prevFiles) =>
      prevFiles.map((file) =>
        file.id === selectedImage.id ? { ...file, url: editedImage } : file
      )
    );
    // Cleanup
    setSelectedImage(null);
    setIsModalOpen(false);
    setCompletedCrop(null);
    setHighlights([]);
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const companyResponse = await getSettingsCompanyDetails();
        setSettingCompanyDetails(companyResponse.data.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchSettings();
  }, []);

  const handleRemoveBgImageeForm = async (fileId) => {
    const selectedImage = images.find((file) => file.id === fileId);

    if (!selectedImage) {
      toast.error(t("pi_toast_please_select_image_first"));
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedImage.file);

    setIsLoading(true);

    try {
      const response = await fetch(
        "https://images.tp.software/remove-bg/",
        {
          method: "POST",
          body: formData,
        }
      );

      let imageBlob;

      if (!response.ok) throw new Error("Failed to remove background");
      imageBlob = await response.blob();

      const imageUrl = URL.createObjectURL(imageBlob);
      setImage((prevFiles) =>
        prevFiles.map((file) =>
          file.id === fileId
            ? { ...file, url: imageUrl, file: imageBlob }
            : file
        )
      );

      toast.success(t("pi_toast_bg_removed_ok"));
      setSelectedImage({ url: imageUrl });
      setIsModalOpen(false);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddBgImageForm = async (fileId) => {
    const selectedImage = images.find((file) => file.id === fileId);
    const proxyUrl = "https://api.allorigins.win/raw?url=";

    const response = await fetch(
      proxyUrl + encodeURIComponent(settingCompanyDetails?.default_image)
    );
    if (!response.ok) throw new Error("Default image is not available");

    const blob = await response.blob();
    const file = new File([blob], "background.jpg", { type: blob.type });

    if (!selectedImage) {
      toast.error(t("pi_toast_please_select_image_first"));
      return;
    }

    const formData = new FormData();
    formData.append("main_image", selectedImage?.file);
    formData.append("bg_image", file);

    setIsLoadings(true);

    try {
      const response = await fetch("https://images.tp.software/add-bg/", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Failed to remove background");

      const imageBlob = await response.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      setImage((prevFiles) =>
        prevFiles.map((file) =>
          file.id === fileId
            ? { ...file, url: imageUrl, file: imageBlob }
            : file
        )
      );

      toast.success(t("pi_toast_bg_added_ok"));
      setSelectedImage({ url: imageUrl });
      setIsModalOpen(false);
    } catch (error) {
    } finally {
      setIsLoadings(false);
    }
  };

  const handleAddBgColoreForm = async (fileId) => {
    const selectedImage = images.find((file) => file.id === fileId);

    if (!selectedImage) {
      toast.error(t("pi_toast_please_select_image_first"));
      return;
    }

    const encodedColor = encodeURIComponent(
      settingCompanyDetails?.default_color
    );

    const formData = new FormData();
    formData.append("main_image", selectedImage.file);

    setIsLoadingColor(true);

    try {
      const response = await fetch(
        `https://images.tp.software/add-bg-color/?hex_color=${encodedColor}`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) throw new Error("Failed to add background color");

      const imageBlob = await response.blob();
      const imageUrl = URL.createObjectURL(imageBlob);
      setImage((prevFiles) =>
        prevFiles.map((file) =>
          file.id === fileId
            ? { ...file, url: imageUrl, file: imageBlob }
            : file
        )
      );

      toast.success(t("pi_toast_bg_color_added_ok"));
      setSelectedImage({ url: imageUrl });
      setIsModalOpen(false);
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setIsLoadingColor(false);
    }
  };

  const hasProcessedRef = useRef(false);

  useEffect(() => {
    if (hasProcessedRef.current) return;

    if (!images?.length || !settingCompanyDetails?.edit_photo_type) return;

    hasProcessedRef.current = true;

    const type = settingCompanyDetails.edit_photo_type;

    images.forEach((file) => {
      if (type === 2) {
        handleRemoveBgImageeForm(file.id);
      } else if (type === 3) {
        handleAddBgColoreForm(file.id);
      } else if (type === 4) {
        handleAddBgImageForm(file.id);
      }
    });
  }, [images, settingCompanyDetails]);

  const handleRotate = async (fileId) => {
    setRotation((prev) => (prev + 90) % 360);
  };

  const handleSubmitPhoto = (event) => {
    event.preventDefault();
    setIsSubmitting(true); // Start loader

    const formData = new FormData();
    images.forEach((item, index) => {
      if (item.file instanceof File || item.file instanceof Blob) {
        const filename = item.file.name || `image-${index}.png`;
        formData.append("files", item.file, filename);
      } else {
        console.error("Invalid file object:", item.file);
      }
    });

    let ktype = "0";

    const vehicleData = state?.state?.item?.vehicle_data;

    if (vehicleData) {
      if (vehicleData.ATYPNR !== "0") {
        ktype = vehicleData.ATYPNR;
      } else if (vehicleData.KTYPNR !== "0") {
        ktype = vehicleData.KTYPNR;
      } else if (vehicleData.NTYPNR !== "0") {
        ktype = vehicleData.NTYPNR;
      }
    }

    formData.append("ktype", ktype);

    formData.append(
      "tecDocModel",
      state?.state?.item?.vehicle_data?.id_tec_doc
        ? state?.state?.item?.vehicle_data?.id_tec_doc
        : 0
    );
    formData.append("language", LangData ? LangData : "pt");
    formData.append("vatIncluded", settingCompanyDetails?.vat_included);
    formData.append(
      "regNumberCompany",
      companyId?.company_details?.company_registration_number
    );

    createPartsByPhoto(formData)
      .then((response) => {
        toast.success(response.data.message);
        setImage(response.data.data?.params?.photos);
        SetPreviewUrl(response.data.data?.params?.photos);
        setPartsByPhotoData(response.data.data);
        setCurrentStep(2);
      })
      .catch((err) => {
        toast.error(Utils.getErrorMessage(err));
      })
      .finally(() => {
        setIsSubmitting(false); // Stop loader
      });
  };

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2 px-2">
            <div className="col-sm-6">
              <h1>{t("add_images")}</h1>
            </div>
            <div className="col-sm-6"></div>
          </div>
        </div>
      </section>
      <section className="content">
        {currentStep === 1 && (
          <div className="container-fluid">
            <div className="row justify-content-start">
              <div className="col-md-12">
                <div className="card card-primary">
                  <Form method={`post`} onSubmit={handleSubmitPhoto}>
                    <div className="card-body">
                      <div className="row justify-content-start">
                        <div className="col-lg-12">
                          <Card className="text-center">
                            <Card.Header className="cardHeader">
                              <div className="d-flex">
                                <div className="AddPh6">
                                  <h6>{t("upload_part_images")}</h6>
                                </div>
                              </div>
                            </Card.Header>
                            <Card.Body className="cardBody">
                              <div className="row">
                                <div className="col-lg-12 addPartField">
                                  <div
                                    className="col-lg-12 addPartField"
                                    style={{
                                      height: "400px",
                                      overflowY: "auto",
                                      overflowX: "hidden",
                                    }}
                                  >
                                    <label className="partsLabel col-form-label ml-2 Label-my form-label">
                                      {t("upload_images")}
                                    </label>

                                    <div className="input-div">
                                      <div className="avatar-upload">
                                        <div className="avatar-edit">
                                          <input
                                            ref={fileInputRef}
                                            className="inputFile"
                                            type="file"
                                            id="imageuploadAbout"
                                            onChange={handleAboutFileChange}
                                            name="file"
                                            accept=".jpg,.jpeg,.png"
                                            multiple
                                          />
                                          <label htmlFor="imageuploadAbout"></label>
                                        </div>

                                        <div className="partImages partImages_mob mt-2 d-flex flex-wrap">
                                          {images?.map((file, index) => (
                                            <div
                                              key={index}
                                              className="preview-item"
                                              style={{
                                                position: "relative",
                                                display: "inline-block",
                                                margin: "5px",
                                              }}
                                            >
                                              <img
                                                onClick={() =>
                                                  toggleEnlarge(index)
                                                }
                                                className={`image ${
                                                  enlargedIndexes.includes(
                                                    index
                                                  )
                                                    ? "enlarged"
                                                    : ""
                                                }`}
                                                src={previewUrl[index]}
                                                alt={`Preview ${index}`}
                                                style={{
                                                  width: "100px",
                                                  height: "100px",
                                                  border: "1px solid",
                                                  borderRadius: "5px",
                                                  objectFit: "cover",
                                                }}
                                              />

                                              <i
                                                className="editImageBtn bi bi-pen-fill"
                                                style={{
                                                  position: "absolute",
                                                  top: "5px",
                                                  left: "5px",
                                                  cursor: "pointer",
                                                  color: "blue",
                                                  backgroundColor: "white",
                                                  borderRadius: "50%",
                                                  padding: "2px",
                                                  zIndex: 2,
                                                }}
                                                onClick={() =>
                                                  handleImageSelect(file)
                                                }
                                              ></i>

                                              <i
                                                className="removeImageBtn bi bi-x"
                                                style={{
                                                  position: "absolute",
                                                  top: "5px",
                                                  right: "5px",
                                                  cursor: "pointer",
                                                  color: "red",
                                                  backgroundColor: "white",
                                                  borderRadius: "50%",
                                                  padding: "2px",
                                                  zIndex: 2,
                                                }}
                                                onClick={() =>
                                                  handleRemoveFile(index)
                                                }
                                              ></i>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>

                          <div className="d-flex flex-row mt-3 justify-content-end">
                            <Button
                              className="me-2 mb-2 btn-primary"
                              type="submit"
                              disabled={isSubmitting}
                            >
                              {isSubmitting ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-1"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  {t("saving")}...
                                </>
                              ) : (
                                t("save")
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <AutoFillVehicle
            partsByPhotoData={partsByPhotoData}
            vehicleData={state}
          />
        )}

        <Modal isOpen={isModalOpen} toggle={closeModal} size="lg">
          <Form method="post">
            <ModalHeader toggle={closeModal}>Image Edit</ModalHeader>
            {selectedImage && (
              <ModalBody>
                <div className="modal-body" style={{ marginBottom: "100px" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "400px",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{
                        position: "relative",
                        display: "inline-block",
                        width: "100%",
                        height: "394px",
                      }}
                    >
                      <ReactCrop
                        crop={crop}
                        onImageLoaded={onImageLoad}
                        onChange={(newCrop) => setCrop(newCrop)}
                        onComplete={(c) => setCompletedCrop(c)}
                        disabled={!isCropping}
                      >
                        <img
                          src={selectedImage.url}
                          ref={imgRef}
                          style={{
                            transform: `rotate(${rotation}deg)`,
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "cover",
                            height: "394px",
                            display: selectedImage ? "block" : "none",
                          }}
                          alt="Crop preview"
                          onClick={onImageClick}
                        />
                      </ReactCrop>
                      {isDrawing && !isCropping && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                          }}
                        >
                          <ReactSketchCanvas
                            ref={canvasRef}
                            style={{
                              width: "100%",
                              height: "100%",
                              position: "absolute",
                              top: 0,
                              left: 0,
                            }}
                            canvasColor="transparent"
                            allowOnlyPointerType="all"
                          />
                        </div>
                      )}
                    </div>

                    <div className="row" style={{ marginTop: "40px" }}>
                      <div className="col-md-2">
                        <i
                          className="bi bi-crop"
                          style={{
                            fontSize: "22px",
                            cursor: "pointer",
                            color: isCropping ? "blue" : "black",
                          }}
                          onClick={handleCrop}
                        ></i>
                      </div>

                      <div className="col-md-2">
                        <i
                          className="bi bi-brush"
                          style={{
                            fontSize: "22px",
                            cursor: "pointer",
                            color: isDrawing ? "blue" : "black",
                          }}
                          onClick={() => saveDrawing(selectedImage.id)}
                        ></i>
                      </div>

                      <div className="col-md-2">
                        <i
                          className="bi bi-arrow-counterclockwise"
                          style={{ fontSize: "22px", cursor: "pointer" }}
                          onClick={() => handleRotate(selectedImage.id)}
                        ></i>
                      </div>

                      <div className="col-md-2">
                        <i
                          className="bi bi-image"
                          style={{ fontSize: "22px", cursor: "pointer" }}
                          onClick={() =>
                            handleRemoveBgImageeForm(selectedImage.id)
                          }
                          disabled={isLoading}
                        ></i>
                      </div>

                      <div className="col-md-2">
                        <i
                          className="bi bi-image"
                          style={{ fontSize: "22px", cursor: "pointer" }}
                          onClick={(e) =>
                            handleAddBgImageForm(selectedImage.id)
                          }
                          disabled={isLoadings}
                        ></i>
                      </div>

                      <div className="col-md-2">
                        <i
                          className="bi bi-paint-bucket"
                          style={{ fontSize: "22px", cursor: "pointer" }}
                          onClick={() =>
                            handleAddBgColoreForm(selectedImage.id)
                          }
                        ></i>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-2">
                        <span>Crop</span>
                      </div>

                      <div className="col-md-2">
                        <span>Brush</span>
                      </div>

                      <div className="col-md-2">
                        <span>Rotate</span>
                      </div>

                      <div className="col-md-2">
                        <span>
                          {isLoading ? "Processing..." : "Remove Back"}
                        </span>
                      </div>

                      <div className="col-md-2">
                        <span>
                          {" "}
                          {isLoadings ? "Processing..." : "Add Back"}
                        </span>
                      </div>

                      <div className="col-md-2">
                        <span>
                          {isLoadingColor ? "Processing..." : "Add Color"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </ModalBody>
            )}

            <ModalFooter>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeModal}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={getCroppedImage}
              >
                Apply Changes
              </button>
            </ModalFooter>
          </Form>
        </Modal>
      </section>
    </div>
  );
};

export default AutofillParts;
